import React, { useState, useEffect } from "react";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import Stack from "@mui/joy/Stack";
import Edit from "@mui/icons-material/Edit";
import Typography from "@mui/joy/Typography";
import Autocomplete from "@mui/joy/Autocomplete";
import { useFetchUserContacts } from "../../api/contacts";
import { ChatProps, IdentityProps } from "../../utils/chat-types";
import { useEditGroup } from "../../api/chat";
import useSocketStore from "../../stores/store-socket";

/**
 * Props interface for the EditGroupModal component.
 * 
 * @interface EditGroupModalProps
 * @property {string} id - The unique identifier of the group
 * @property {string} name - The current name of the group
 * @property {IdentityProps[] | undefined} members - Array of current group members
 */
type EditGroupModalProps = {
  id: string;
  name: string;
  members: IdentityProps[] | undefined;
};

/**
 * A modal component for editing group chat details.
 * Allows users to modify the group name and members.
 * 
 * @component
 * @param {EditGroupModalProps} props - The props for the EditGroupModal component
 * @returns {JSX.Element} A modal dialog for editing group information
 * 
 * @example
 * ```tsx
 * <EditGroupModal 
 *   id="group123" 
 *   name="Team Chat" 
 *   members={[{ id: "1", name: "John", username: "john" }]} 
 * />
 * ```
 */
export default function EditGroupModal({
  id,
  name,
  members,
}: EditGroupModalProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [editedGroupName, setEditedGroupName] = useState<string>(name);
  const [editedMembers, setEditedMembers] = useState<string[]>(
    members ? members.map((member) => member.username) : []
  );

  useEffect(() => {
    // Update the state when the props change
    setEditedGroupName(name);
    setEditedMembers(members ? members.map((member) => member.username) : []);
  }, [name, members]); // Listen for changes in 'name' and 'members' props

  const usersData = useFetchUserContacts(); // Fetch user data
  const editGroup = useEditGroup(); // Initialize the useEditGroup hook

  const userNames = usersData.data?.data
    ? usersData.data.data.map((user: IdentityProps) => user.name)
    : [];

  const {
    setSocketConnectionState,
    socketConnectionState,
    socketRefCurrentState,
    dialogsListState,
    selectedChat,
    setSelectedChat,
  } = useSocketStore();

  /**
   * Handles changes to the selected members in the autocomplete component.
   * 
   * @param {React.ChangeEvent<{}>} event - The change event
   * @param {string[]} newValue - The new array of selected member names
   */
  const handleMembersChange = (
    event: React.ChangeEvent<{}>,
    newValue: string[]
  ) => {
    setEditedMembers(newValue);
  };

  /**
   * Handles the form submission for editing the group.
   * Updates the group name and members in the backend and local state.
   * 
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event
   */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setOpen(false);

    // Convert edited member names to their corresponding IDs
    const editedMemberIds = usersData.data?.data
      ?.filter((user: IdentityProps) => editedMembers.includes(user.name))
      .map((user: IdentityProps) => user.id);

    if (editedMemberIds && editedGroupName) {
      try {
        const result = await editGroup.mutateAsync({
          // Use .mutateAsync to await the mutation
          groupId: id,
          newParticipantIds: editedMemberIds,
          newGroupName: editedGroupName,
        });

        if (result.success) {
          if (dialogsListState && members) {
            // Create a new list of UserProps based on the selected member usernames
            const updatedMembers: IdentityProps[] =
              usersData.data?.data
                ?.filter((user: IdentityProps) =>
                  editedMembers.includes(user.name)
                )
                .map((user: IdentityProps) => ({
                  ...user,
                  username: user.name,
                })) || [];

            const updatedDialogsList = dialogsListState.map(
              (dialog: ChatProps) => {
                if (dialog.id === `group_${id}`) {
                  // Update the group's name and members
                  return {
                    ...dialog,
                    sender: {
                      ...dialog.sender,
                      username: editedGroupName,
                      name: editedGroupName,
                    },
                    members: updatedMembers,
                  };
                }
                return dialog;
              }
            );
            setSocketConnectionState(
              socketConnectionState,
              socketRefCurrentState,
              updatedDialogsList
            );
            if (selectedChat)
              setSelectedChat({
                ...selectedChat,
                sender: {
                  ...selectedChat.sender,
                  username: editedGroupName,
                  name: editedGroupName,
                },
                members: updatedMembers,
              });
          }
        }
      } catch (error) {
        console.error("Error editing group:", error);
      }
    }
  };

  return (
    <React.Fragment>
      <Button variant="outlined" color="neutral" onClick={() => setOpen(true)}>
        <Edit />
      </Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          aria-labelledby="basic-modal-dialog-title"
          aria-describedby="basic-modal-dialog-description"
          sx={{ maxWidth: 500 }}
        >
          <Typography id="basic-modal-dialog-title" component="h2">
            Gruppe bearbeiten
          </Typography>
          <Typography
            id="basic-modal-dialog-description"
            textColor="text.tertiary"
          >
            Aktualisieren Sie die Informationen der Gruppe.
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  autoFocus
                  required
                  value={editedGroupName}
                  onChange={(event) => setEditedGroupName(event.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Mitglieder</FormLabel>
                {usersData.isLoading ? (
                  <div>Loading...</div>
                ) : (
                  <Autocomplete
                    disableCloseOnSelect
                    multiple
                    id="tags-default"
                    options={userNames}
                    value={editedMembers}
                    onChange={handleMembersChange}
                    placeholder="Wählen Sie Mitglieder aus..."
                  />
                )}
              </FormControl>
              <Stack direction="row" justifyContent="space-between">
                <Button variant="outlined" onClick={() => setOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">Bestätigen</Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </React.Fragment>
  );
}
